/**
 * Builds an activity catalog from the variant data.
 * Creates a mapping of activity codes to their full names.
 * 
 * @param {Array} variants - Array of variant objects
 * @param {Object} activityCatalog - Activity catalog from backend (if available)
 * @returns {Object} - Map of activity_code to { code, name }
 */
export const buildActivityCatalog = (variants, activityCatalog = null) => {
  const catalog = {};

  // If backend provides activity catalog, use it
  if (activityCatalog && typeof activityCatalog === 'object') {
    Object.entries(activityCatalog).forEach(([code, name]) => {
      catalog[code] = { code, name };
    });
    return catalog;
  }

  // Otherwise, build from variant data
  if (!Array.isArray(variants)) {
    return catalog;
  }

  variants.forEach(variant => {
    if (variant.activity_sequence && Array.isArray(variant.activity_sequence)) {
      variant.activity_sequence.forEach(activity => {
        const code = activity.activity_code || activity.code;
        const name = activity.activity_name || activity.name || code;
        
        if (code && !catalog[code]) {
          catalog[code] = { code, name };
        }
      });
    }
  });

  return catalog;
};

/**
 * Gets unique activity codes from all variants in current view.
 * 
 * @param {Array} variants - Array of variant objects
 * @returns {Array<string>} - Sorted array of unique activity codes
 */
export const getUniqueActivityCodes = (variants) => {
  const codes = new Set();

  if (!Array.isArray(variants)) {
    return [];
  }

  variants.forEach(variant => {
    if (variant.activity_sequence && Array.isArray(variant.activity_sequence)) {
      variant.activity_sequence.forEach(activity => {
        const code = activity.activity_code || activity.code;
        if (code) {
          codes.add(code);
        }
      });
    }
  });

  return Array.from(codes).sort();
};

/**
 * Sorts activities by their position in the sequence.
 * 
 * @param {Array} activitySequence - Array of activity objects with position field
 * @returns {Array} - Sorted array of activities
 */
export const sortActivitiesByPosition = (activitySequence) => {
  if (!Array.isArray(activitySequence)) {
    return [];
  }

  return [...activitySequence].sort((a, b) => {
    const posA = a.position ?? Infinity;
    const posB = b.position ?? Infinity;
    return posA - posB;
  });
};

/**
 * Filters the activity catalog to only include activities present in variants.
 * 
 * @param {Object} fullCatalog - Full activity catalog
 * @param {Array} variants - Array of variant objects
 * @returns {Object} - Filtered catalog
 */
export const filterCatalogByVariants = (fullCatalog, variants) => {
  const presentCodes = getUniqueActivityCodes(variants);
  const filtered = {};

  console.log('🔍 filterCatalogByVariants called:', {
    fullCatalogKeys: Object.keys(fullCatalog),
    presentCodes,
    variantsCount: variants.length
  });

  presentCodes.forEach(code => {
    if (fullCatalog[code]) {
      filtered[code] = fullCatalog[code];
    } else {
      console.warn(`⚠️ Activity code "${code}" found in variants but not in fullCatalog`);
    }
  });

  console.log('🔍 filterCatalogByVariants result:', {
    filteredKeys: Object.keys(filtered),
    filteredCount: Object.keys(filtered).length
  });

  return filtered;
};
